import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { useNavigationType, useParams } from "react-router-dom";
import { debounce } from "lodash";

import StatementTable from "Common/Tables/StatementTable";
import Loader from "Common/Loader";
import { authServices } from "Utils/auth/services";
import { currentDateTime, getStartTimeOfDay } from "Utils/datefilter";
import { generateOptions } from "Utils/searchFilter";

interface StatementProps {
  filter?: boolean;
  activeTab: string;
  isReport?: boolean;
}

const Statement = ({ filter, activeTab, isReport = false }: StatementProps) => {
  const { userid } = useParams();
  const navigationType = useNavigationType();

  const today = new Date();
  const initialStartDate = getStartTimeOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)); // 7 days ago
  const initialEndDate = currentDateTime();

  const [statementData, setStatementData] = useState<StatementListData[]>([]);
  const [startDate, setStartDate] = useState<Date | string>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | string>(initialEndDate);
  const [statementType, setStatementType] = useState<string>("");
  const [limit, setLimit] = useState<number>(50);
  const [page, setPage] = useState<number>(1);
  const [listTotal, setListTotal] = useState<ProfitLossTotal>({ total: 0, page: 0 });
  const [usersData, setUsersData] = useState<SearchUsersListData[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isInitialRender = useRef(true);

  const userOptions = useMemo(
    () => generateOptions(usersData || [], "_id", "user_name"),
    [usersData]
  );

  // Persist dates in localStorage
  useEffect(() => {
    if (!isInitialRender.current) {
      localStorage.setItem("statementStart", startDate.toString());
      localStorage.setItem("statementEnd", endDate.toString());
    } else {
      isInitialRender.current = false;
    }
  }, [startDate, endDate]);

  // Fetch users
  const fetchOptions = useMemo(
    () =>
      debounce(async (input: string) => {
        if (input.length >= 3) {
          const { response } = await authServices.getUserList({ user_name: input });
          setUsersData(response?.data || []);
        }
      }, 300),
    []
  );

  useEffect(() => {
    fetchOptions(inputValue);
  }, [inputValue]);

  // Fetch statement data
  const getStatementList = async (filters: any = {}) => {
    setIsLoading(true);
    const { response } = await authServices.getStatementList({
      limit,
      page,
      ...(userid && userid !== "null" && { user_id: userid }),
      ...filters,
    });
    setIsLoading(false);

    if (response?.data?.length > 0) {
      const [data] = response.data;
      setStatementData(data?.data || []);
      setListTotal({
        total: data?.metadata?.[0]?.total / data?.metadata?.[0]?.limit || 0,
        page: data?.metadata?.[0]?.page || 1,
      });
    } else {
      setStatementData([]);
      setListTotal({ total: 0, page: 1 });
    }
  };

  // Initial fetch
  useEffect(() => {
    let start = navigationType === "POP" ? localStorage.getItem("statementStart") : startDate;
    let end = navigationType === "POP" ? localStorage.getItem("statementEnd") : endDate;

    if (start) setStartDate(new Date(start));
    if (end) setEndDate(new Date(end));

    if (activeTab === "Statement") {
      getStatementList({
        ...(!isReport && {
          from_date: new Date(start || ''),
          to_date: new Date(end || ''),
        }),
      });
    }
  }, [activeTab, limit]);

  const handleFilterSubmit = () => {
    setPage(1);
    const filterData: any = {
      from_date: startDate,
      to_date: endDate,
      page: 1,
    };
    if (statementType) filterData.statement_type = statementType;
    if (selectedUser) filterData.user_id = selectedUser;

    getStatementList(filterData);
  };

  const handleClearSelection = () => {
    const resetStart = getStartTimeOfDay(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    const resetEnd = currentDateTime();

    setPage(1);
    setStartDate(resetStart);
    setEndDate(resetEnd);
    setStatementType("");
    setUsersData([]);
    setSelectedUser("");

    getStatementList({
      ...(!isReport && {
        from_date: resetStart,
        to_date: resetEnd,
      }),
      page: 1,
    });
  };

  const handleStatementType = (type: string) => {
    setPage(1);
    setStatementType(type);

    const filterData: any = {
      ...(!isReport && { from_date: startDate, to_date: endDate }),
      page: 1,
    };
    if (type) filterData.statement_type = type;
    if (selectedUser) filterData.user_id = selectedUser;

    getStatementList(filterData);
  };

  const handlePageFilter = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);

    const filterData: any = {
      ...(!isReport && { from_date: startDate, to_date: endDate }),
      page: newPage,
      limit: newLimit,
    };
    if (statementType) filterData.statement_type = statementType;
    if (selectedUser) filterData.user_id = selectedUser;

    getStatementList(filterData);
  };

  return (
    <>
      {filter && (
        <Card>
          <Card.Body>
            <Form>
              <div className="d-flex flex-wrap mb-3 align-items-center">
                {[
                  { id: "", label: "Statement" },
                  { id: "1", label: "Free Chips" },
                  { id: "2", label: "Profit Loss" },
                  { id: "3", label: "Commission" },
                  { id: "4", label: "Settlement" },
                  // { id: "8", label: "UpLine King" },
                ].map(({ id, label }) => (
                  <div className="form-check me-3" key={id}>
                    <Form.Check
                      type="radio"
                      name="statement"
                      id={label}
                      checked={statementType === id}
                      onChange={() => handleStatementType(id)}
                    />
                    <Form.Label className="form-check-label" htmlFor={label}>
                      {label}
                    </Form.Label>
                  </div>
                ))}
              </div>
              <Row className="gy-3 align-items-end">
                <Col lg={9}>
                  <Row className="gap-3 gap-sm-0">
                    <Col lg={4}>
                      <Form.Label>Start Date</Form.Label>
                      <Flatpickr
                        className="form-control"
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i:s",
                          maxDate: endDate,
                        }}
                        value={startDate}
                        onChange={([date]: [Date]) => setStartDate(date)}
                      />
                    </Col>
                    <Col lg={4}>
                      <Form.Label>End Date</Form.Label>
                      <Flatpickr
                        className="form-control"
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i:s",
                          maxDate: currentDateTime(),
                        }}
                        value={endDate}
                        onChange={([date]: [Date]) => setEndDate(date)}
                      />
                    </Col>
                    <Col lg={4}>
                      <Form.Label>Search By Username</Form.Label>
                      <Select
                        isClearable
                        options={userOptions}
                        value={userOptions.find((opt) => opt.value === selectedUser) || null}
                        className="customSelect"
                        onInputChange={(input:any) =>
                          setInputValue(input?.trim()?.toLowerCase())
                        }
                        onChange={(e: any) => setSelectedUser(e?.value || "")}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Button className="me-2 btn-sm" style={{ height: "38px" }} onClick={handleFilterSubmit}>
                    <i className="fs-lg align-middle ri-filter-line"></i> Submit
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="btn-sm"
                    style={{ height: "38px" }}
                    onClick={handleClearSelection}
                  >
                    <i className="fs-lg align-middle ri-refresh-line"></i> Clear
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}
      <Card>
        <Card.Body>
          <StatementTable
            statementData={statementData}
            page={page}
            limit={limit}
            listTotal={listTotal}
            startDate={startDate}
            endDate={endDate}
            handlePageFilter={handlePageFilter}
          />
        </Card.Body>
      </Card>
      {isLoading && <Loader />}
    </>
  );
};

export default Statement;
